import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, Loader2, MailWarning, XCircle } from "lucide-react";
import { api, API } from "@/lib/api";
import { SupportButton } from "@/components/SupportModal";

export default function Verify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading");
  const [email, setEmail] = useState("");
  const [verifiedToken, setVerifiedToken] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [devLink, setDevLink] = useState("");
  const [message, setMessage] = useState("");
  const [build, setBuild] = useState(null);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    api
      .get("/verify", { params: { token } })
      .then(({ data }) => {
        setEmail(data.email);
        setVerifiedToken(data.token);
        localStorage.setItem("cnc_download_token", data.token);
        setState("verified");
        api.get("/build-info").then(({ data: b }) => setBuild(b)).catch(() => {});
      })
      .catch((err) => {
        const status = err.response?.status;
        setState(status === 410 ? "expired" : "invalid");
      });
  }, [token]);

  const resend = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      const { data } = await api.post("/resend", { email: resendEmail });
      if (data.status === "already_verified") {
        setVerifiedToken(data.token);
        setEmail(resendEmail);
        setState("verified");
      } else {
        setDevLink(data.dev_verification_url || "");
        setState("resent");
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || "Could not resend. Try again.");
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-[#F5F5F5] blueprint-grid flex items-center justify-center px-6 py-24" data-testid="verify-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-xl border border-white/15 bg-black/60 backdrop-blur-md p-8 sm:p-12"
      >
        <Link to="/" className="label-mono hover:text-[#FF3B00] transition-colors" data-testid="verify-back-link">
          ← CNCroutersoft
        </Link>

        {state === "loading" && (
          <div className="mt-10 flex items-center gap-3" data-testid="verify-loading">
            <Loader2 className="w-5 h-5 animate-spin text-[#FF3B00]" />
            <span className="font-mono2 text-sm text-white/60">Confirming your email…</span>
          </div>
        )}

        {state === "verified" && (
          <div className="mt-10" data-testid="verify-success">
            <CheckCircle2 className="w-10 h-10 text-[#00FF41] mb-6" />
            <h1 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl leading-[0.95]">
              Confirmed.
            </h1>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              <span className="text-white font-medium">{email}</span> is verified. Your build is unlocked —
              this browser will remember it on the download page too.
            </p>
            <a
              data-testid="verify-download-button"
              href={`${API}/download?token=${verifiedToken}`}
              className="btn-forge mt-8"
            >
              <Download className="w-4 h-4" /> Download CNCroutersoft 1.0.0
            </a>
            <p className="mt-3 font-mono2 text-[11px] text-white/40" data-testid="verify-build-info">
              {build
                ? `${build.filename} · ${(build.size / 1048576).toFixed(1)} MB · uploaded ${build.updated_at.slice(0, 16).replace("T", " ")} UTC · Windows 10/11 64-bit`
                : "Windows 10/11 64-bit"}
            </p>
          </div>
        )}

        {(state === "expired" || state === "invalid") && (
          <div className="mt-10" data-testid="verify-failed">
            <XCircle className="w-10 h-10 text-[#FF3B00] mb-6" />
            <h1 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl leading-[0.95]">
              {state === "expired" ? "Link expired." : "Bad link."}
            </h1>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              {state === "expired"
                ? "Verification links live for 24 hours. Enter your email and we'll send a fresh one."
                : "This verification link is invalid or was already replaced. Request a new one below."}
            </p>
            <form onSubmit={resend} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="resend-form">
              <input
                data-testid="resend-email-input"
                type="email"
                required
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="you@workshop.com"
                className="flex-1 bg-transparent border-b border-white/25 focus:border-[#FF3B00] outline-none py-3 font-mono2 text-sm placeholder:text-white/25 transition-colors"
              />
              <button data-testid="resend-submit-button" type="submit" className="btn-forge">
                Resend link
              </button>
            </form>
            {message && <p className="mt-3 font-mono2 text-xs text-[#FF3B00]" data-testid="resend-error">{message}</p>}
            <div className="mt-6 flex items-center gap-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-white/35">
              Still stuck?
              <SupportButton testId="verify-support-link" label="Contact support" className="!text-white/60" />
            </div>
          </div>
        )}

        {state === "resent" && (
          <div className="mt-10" data-testid="resend-sent">
            <CheckCircle2 className="w-10 h-10 text-[#00FF41] mb-6" />
            <h1 className="font-display font-bold uppercase tracking-tight text-4xl leading-[0.95]">
              Fresh link sent.
            </h1>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              Check your inbox for the new confirmation link.
            </p>
            {devLink && (
              <div className="mt-6 border border-dashed border-[#F5A623]/50 bg-[#F5A623]/5 p-4" data-testid="resend-mock-inbox">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#F5A623] mb-2 flex items-center gap-2">
                  <MailWarning className="w-3.5 h-3.5" /> Mocked inbox — email sending is disabled in this demo
                </p>
                <a
                  data-testid="resend-mock-link"
                  href={devLink}
                  className="font-mono2 text-xs text-white/80 break-all underline decoration-[#FF3B00] underline-offset-4 hover:text-[#FF3B00] transition-colors"
                >
                  {devLink}
                </a>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </main>
  );
}
